import { ReactNode, useEffect, useRef, useState } from "react";
import styles from "./Stepper.module.scss";
import { Icon } from "../Icon";
import { Button } from "../Button";
import { AnimatePresence, motion } from "framer-motion";

interface StepperProps {
  dark?: boolean;
  /**
   * Controls the orientation of the stepper
   * @default "auto"
   */
  variant?: "auto" | "horizontal" | "vertical"; // TODO: Handle x-overflow if variant is "horizontal"
  steps: {
    step?: number;
    icon?: string;
    title?: string | ReactNode;
    content: string | ReactNode;
    isValid?: boolean;
    onNavAway?: () => void;
    validateStep?: () => Promise<boolean>;
  }[];
  activeStep: number;
  /**
   * If true, the steps become freely navigable and next-button is never disabled
   * Otherwise, the next-button is disabled if the current step is invalid
   * @default true
   */
  allowSkip?: boolean;
  text?: {
    step?: string;
    next?: string;
    prev?: string;
  };
  onChange?: () => void;
  onBlur?: () => void;
  buttons?: {
    finish?: ReactNode;
    next?: ReactNode;
    prev?: ReactNode;
    customRight?: ReactNode;
    customLeft?: ReactNode;
  };
  /**
   * The header level to use (e.g., "h1", "h2", etc.) for accessibility
   * @default "2"
   */
  headerLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  setActiveStep: (step: number) => void;
  keepAllStepsMounted?: boolean;
  containerStyle?: React.CSSProperties;
}

export function Stepper({
  dark,
  variant = "auto",
  steps,
  activeStep,
  allowSkip = true,
  text = {
    step: "Step",
    next: "Next step",
    prev: "Previous step",
  },
  buttons,
  headerLevel = 2,
  keepAllStepsMounted = false,
  containerStyle,
  setActiveStep,
  onChange,
  onBlur,
}: StepperProps) {
  const [isHorizontal, setIsHorizontal] = useState(
    variant === "vertical" ? false : true,
  );
  const [maxAvailableStep, setMaxAvailableStep] = useState<number>(
    activeStep + 1,
  );
  const [modeDetectionCompleted, setModeDetectionCompleted] = useState<boolean>(
    variant !== "auto",
  );
  const [animationDirection, setAnimationDirection] = useState(1); // 1 for forward, -1 for backward
  const containerRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLHeadingElement[]>([]);
  const isFirstRender = useRef(true);

  // Keep internal progression in sync when parent updates activeStep directly.
  useEffect(() => {
    setMaxAvailableStep((prev) => Math.max(prev, activeStep + 1));
  }, [activeStep]);

  const handleNavigate = async (newStep: number) => {
    // Check if navigation should be blocked
    if (newStep >= activeStep) {
      if (!allowSkip && newStep > maxAvailableStep) return;
      const currStep = steps[activeStep];
      const stepIsValid = currStep?.validateStep
        ? await currStep?.validateStep()
        : currStep?.isValid !== undefined
          ? currStep?.isValid
          : undefined;
      if (!allowSkip && stepIsValid === false) return;
    }

    // Validate all previous and current step
    for (const step of steps.slice(0, activeStep + 1)) {
      step.validateStep?.();
    }

    setAnimationDirection(newStep > activeStep ? 1 : -1);
    steps[activeStep]?.onNavAway?.();
    setActiveStep(newStep);
    setMaxAvailableStep((prev) => (prev <= newStep ? newStep + 1 : prev));
  };

  // On vertical stepper, instantly scroll to active step when it changes
  useEffect(() => {
    if (isHorizontal) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const pageScrollContainer = document.getElementById(
      "page-scroll-container",
    );
    const stepperTop =
      stepsRef.current[activeStep]?.getBoundingClientRect().top || 0;
    const offset = pageScrollContainer
      ? pageScrollContainer.getBoundingClientRect().top
      : 0;
    pageScrollContainer?.scrollTo({
      top: stepperTop + (pageScrollContainer.scrollTop - offset) - 20,
      behavior: "instant",
    });
  }, [activeStep]);

  useEffect(() => {
    if (variant !== "auto" || steps.length <= 0) return;

    let computedStepsWidth: number | null = null;

    const calculateStepsWidth = () => {
      if (stepsRef.current.length === 0) return;
      const stepCount = stepsRef.current.length;
      const horizontalGap = (stepCount - 1) * 24; // 24px gap between steps
      const horizontalPadding = (stepCount - 1) * 24 * 2; // 24px left/right padding except for first and last step

      const totalStepWidth = stepsRef.current.reduce((sum, stepEl) => {
        const computedStyles = getComputedStyle(stepEl);
        const contentWidth = Math.ceil(
          stepEl.offsetWidth -
            parseFloat(computedStyles.paddingLeft) -
            parseFloat(computedStyles.paddingRight),
        );
        return sum + contentWidth;
      }, 0);
      computedStepsWidth = totalStepWidth + horizontalGap + horizontalPadding;
    };

    const checkOverflow = () => {
      // Check if steps fit horizontally
      if (!containerRef.current || computedStepsWidth === null) return;
      const containerWidth = containerRef.current.clientWidth;
      const newIsHorizontal = computedStepsWidth <= containerWidth;
      setIsHorizontal((prev) =>
        prev !== newIsHorizontal ? newIsHorizontal : prev,
      );
      setModeDetectionCompleted(true);
    };

    const resizeObserver = new ResizeObserver(() =>
      requestAnimationFrame(checkOverflow),
    );

    // Compute step widths only once when refs are available
    const refObserver = new MutationObserver(() => {
      if (stepsRef.current.every((el) => el)) {
        calculateStepsWidth();
        checkOverflow(); // Ensure correct mode initially
        refObserver.disconnect(); // Stop observing after first calculation
      }
    });

    // Ensure recalculation after hot-reload
    const forceRecalculate = () => {
      computedStepsWidth = null;
      calculateStepsWidth();
      checkOverflow();
    };

    refObserver.observe(document.body, { childList: true, subtree: true });
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    forceRecalculate();

    return () => {
      resizeObserver.disconnect();
      refObserver.disconnect();
    };
  }, [steps]);

  const stepAnimationVariants = {
    enter: (direction: number) => ({
      x: isHorizontal ? (direction > 0 ? "100%" : "-100%") : 0,
      y: isHorizontal ? 0 : direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      x: 0,
      y: 0,
      opacity: 1,
      transition: { duration: 0.3 },
    },
    exit: (direction: number) => ({
      x: isHorizontal ? (direction > 0 ? "-100%" : "100%") : 0,
      y: isHorizontal ? 0 : direction > 0 ? "-100%" : "100%",
      opacity: 0,
      transition: { duration: 0.3 },
    }),
  };

  return (
    <div
      ref={containerRef}
      className={`${isHorizontal ? styles.horizontal : styles.vertical} ${
        dark ? styles.dark : ""
      }`}
      style={{
        position: "relative",
        visibility: modeDetectionCompleted ? "visible" : "hidden",
        ...containerStyle,
      }}
      onChange={() =>
        steps[activeStep].isValid === false ? onChange?.() : undefined
      }
      onBlur={() =>
        steps[activeStep].isValid === false ? onBlur?.() : undefined
      }
    >
      {steps.map((step, index) => (
        <p
          ref={(el) => {
            if (el) stepsRef.current[index] = el;
          }}
          key={`step-${step.title || index}-header`}
          className={styles.stepHeader}
          role="heading"
          aria-level={headerLevel}
        >
          <button
            className={styles.stepButton}
            style={
              !allowSkip
                ? {
                    cursor:
                      // user can always navigate back, but only forward if current step is valid
                      index < maxAvailableStep ||
                      step.isValid === true ||
                      (index === maxAvailableStep &&
                        steps[activeStep].isValid !== false)
                        ? "pointer"
                        : "not-allowed",
                  }
                : {}
            }
            onClick={(e) => {
              e.preventDefault();
              handleNavigate(index);
            }}
          >
            <span
              className={`${styles.stepNumber} ${
                index === activeStep ? styles.active : ""
              } ${step.isValid ? styles.valid : step.isValid === false ? styles.invalid : ""}`}
              aria-hidden
            >
              {step.icon ? (
                <Icon name={step.icon} />
              ) : index === activeStep ? (
                index + 1
              ) : step.isValid ? (
                <Icon name="done" />
              ) : step.isValid === false ? (
                <Icon name="emergency" />
              ) : (
                index + 1
              )}
            </span>
            <span className="sr-only">
              {text.step} {index + 1} / {steps.length}:
            </span>
            {!!step.title && (
              <span style={dark ? { color: "white" } : { color: "black" }}>
                {step.title}
              </span>
            )}
          </button>
        </p>
      ))}
      <div className={styles.lineBetweenSteps}></div>
      <div
        className={styles.contentContainer}
        style={{
          gridRow: isHorizontal ? "2" : activeStep + 2,
          marginTop: isHorizontal
            ? ""
            : steps[activeStep].title
              ? "calc(var(--wlcm-spacing-sm) * -1)" // place under title
              : "calc((35px + var(--wlcm-spacing-sm)) * -1)", // place right next to step nr.
        }}
      >
        {keepAllStepsMounted && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              opacity: 0,
              pointerEvents: "none",
              width: "100%",
              height: 0,
              overflow: "hidden",
              zIndex: -1,
            }}
          >
            {steps
              .filter((_, index) => index !== activeStep)
              .map((step, index) => (
                <div key={`step-${step.step || index}-content`}>
                  {step.content}
                </div>
              ))}
          </div>
        )}
        <AnimatePresence
          custom={animationDirection}
          initial={false}
          mode="popLayout"
        >
          <motion.div
            key={`step-${activeStep}-content`}
            variants={stepAnimationVariants}
            custom={animationDirection}
            initial="enter"
            animate="center"
            exit="exit"
            className={styles.content}
          >
            {steps[activeStep].content}
          </motion.div>
        </AnimatePresence>
        <div className={styles.buttons} onClick={(e) => e.preventDefault()}>
          <div className={styles.left}>
            {activeStep > 0 && (
              <>
                {buttons?.prev || (
                  <Button
                    purple
                    icon="arrow_back"
                    onClick={() => handleNavigate(Math.max(0, activeStep - 1))}
                  >
                    {text.prev}
                  </Button>
                )}
              </>
            )}
            {buttons?.customLeft}
          </div>
          <div className={styles.right}>
            {buttons?.customRight}
            {activeStep < steps.length - 1 && (
              <>
                {buttons?.next || (
                  <Button
                    aria-disabled={
                      !allowSkip &&
                      (activeStep >= maxAvailableStep ||
                        steps[activeStep].isValid === false)
                    }
                    purple
                    icon="arrow_forward"
                    iconPosition="right"
                    onClick={() =>
                      handleNavigate(Math.min(steps.length - 1, activeStep + 1))
                    }
                  >
                    {text.next}
                  </Button>
                )}
              </>
            )}
            {activeStep === steps.length - 1 && buttons?.finish}
          </div>
        </div>
      </div>
    </div>
  );
}
